import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Flex,
  Text,
  Heading,
  SimpleGrid,
  Icon,
  Input,
  Select,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Stack,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  Badge,
  TableContainer
} from '@chakra-ui/react';
import {
  FiActivity,
  FiBarChart2,
  FiSearch,
  FiFilter,
  FiCheckCircle,
  FiXCircle,
  FiCalendar
} from 'react-icons/fi';
import axios from 'axios';
import { AppContext } from '../../../context/AppContext';

const DailyStats = () => {
  const [stats, setStats] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [servers] = useState([
    'server1',
    'server2',
    'server3',
    'server4',
    'server5',
  ]);
  const [totalSuccess, setTotalSuccess] = useState(0);
  const [totalFailure, setTotalFailure] = useState(0);
  const { AdminToken } = useContext(AppContext);

  // Colors
  // Colors
  const cardBg = "gray.800";
  const textColor = "white";
  const subTextColor = "gray.400";
  const borderColor = "gray.700";
  const dateRangeBg = "gray.700";
  const tableHeaderBg = "gray.900";
  const rowHoverBg = "gray.700";

  useEffect(() => {
    fetchServiceStats();
  }, [dateTo, dateFrom, searchTerm, filterStatus, AdminToken]);

  const fetchServiceStats = async () => {
    if (!AdminToken) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API}admin/service-stats?search=${searchTerm}&server=${filterStatus}&from=${dateFrom}&to=${dateTo}`,
        {
          headers: {
            authorization: `${AdminToken}`,
          },
        }
      );
      setStats(res.data);
      let tSuccess = 0;
      let tFailure = 0;
      res.data.forEach((x) => {
        tSuccess += x.totalSuccess;
        tFailure += x.totalFailed;
      });
      setTotalFailure(tFailure);
      setTotalSuccess(tSuccess);
    } catch (error) {
      console.error('Error fetching service stats:', error);
    }
  };

  const getSuccessRateColor = (rate) => {
    if (rate >= 95) return 'green.500';
    if (rate >= 90) return 'yellow.500';
    return 'red.500';
  };

  const getSuccessRateBg = (rate) => {
    if (rate >= 95) return 'green.100';
    if (rate >= 90) return 'yellow.100';
    return 'red.100';
  };

  const clearDateFilters = () => {
    setDateFrom(new Date().toISOString().split('T')[0]);
    setDateTo(new Date().toISOString().split('T')[0]);
  };


  return (
    <Box p={[4, 6]} w="full">
      <Stack spacing={6}>
        {/* Header */}
        <Flex direction={['column', 'row']} justify="space-between" align={['start', 'center']} gap={4}>
          <Box>
            <Heading size="lg" color={textColor}>Service Statistics</Heading>
            <Text color={subTextColor}>Monitor service performance and success rates</Text>
          </Box>
        </Flex>

        {/* Summary Cards */}
        <SimpleGrid columns={[2, 2, 4]} spacing={[3, 4, 6]}>
          <StatsCard
            icon={FiBarChart2}
            iconColor="blue.500"
            label="Total Services"
            value={stats.length}
            bg={cardBg}
          />
          <StatsCard
            icon={FiCheckCircle}
            iconColor="green.500"
            label="Total Success"
            value={totalSuccess.toLocaleString()}
            bg={cardBg}
          />
          <StatsCard
            icon={FiXCircle}
            iconColor="red.500"
            label="Total Failed"
            value={totalFailure.toLocaleString()}
            bg={cardBg}
          />
          <StatsCard
            icon={FiActivity}
            iconColor="purple.500"
            label="Success Rate"
            value={`${((totalSuccess / (totalSuccess + totalFailure || 1)) * 100).toFixed(1)}%`}
            valueColor={getSuccessRateColor(((totalSuccess / (totalSuccess + totalFailure || 1)) * 100).toFixed(1))}
            bg={cardBg}
          />
        </SimpleGrid>

        {/* Filters and Search */}
        <Box bg={cardBg} p={6} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
          <Flex direction={['column', 'row']} gap={4} mb={4}>
            <Box flex={1}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Box>
            <Flex gap={2} align="center">
              <Icon as={FiFilter} color="gray.400" />
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                w="auto"
              >
                <option value="all">All Server</option>
                {servers.map((el) => (
                  <option key={el} value={el}>{el}</option>
                ))}
              </Select>
            </Flex>
          </Flex>

          {/* Date Range Filter */}
          <Box pt={4} borderTopWidth="1px" borderColor={borderColor}>
            <Stack spacing={4}>
              <Flex align="center" gap={2}>
                <Icon as={FiCalendar} color="gray.400" />
                <Text fontSize="sm" fontWeight="medium" color={subTextColor}>Date Range:</Text>
              </Flex>

              <Flex direction={['column', 'row']} gap={[3, 4]}>
                <Flex align="center" gap={2} flex={1}>
                  <Text fontSize="sm" fontWeight="medium" minW="50px">From:</Text>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </Flex>
                <Flex align="center" gap={2} flex={1}>
                  <Text fontSize="sm" fontWeight="medium" minW="30px">To:</Text>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </Flex>
              </Flex>

              <Flex direction={['column', 'row']} gap={3} justify="space-between" align={['stretch', 'center']}>
                {(dateFrom || dateTo) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={clearDateFilters}
                  >
                    Clear Dates
                  </Button>
                )}
                {(dateFrom || dateTo) && (
                  <Box bg={dateRangeBg} px={3} py={2} borderRadius="md">
                    <Text fontSize="sm" color={subTextColor}>
                      📅 {new Date(dateFrom).toLocaleDateString()} → {new Date(dateTo).toLocaleDateString()}
                    </Text>
                  </Box>
                )}
              </Flex>
            </Stack>
          </Box>
        </Box>

        {/* Stats Table */}
        <Box bg={cardBg} borderRadius="lg" boxShadow="sm" overflow="hidden" borderWidth="1px" borderColor={borderColor}>
          <TableContainer>
            <Table variant="simple">
              <Thead bg={tableHeaderBg}>
                <Tr>
                  <Th>Service</Th>
                  <Th textAlign="center">Success</Th>
                  <Th textAlign="center">Failed</Th>
                  <Th textAlign="center">Total</Th>
                  <Th textAlign="center">Success Rate</Th>
                </Tr>
              </Thead>
              <Tbody>
                {stats.map((stat, index) => (
                  <Tr key={index} _hover={{ bg: rowHoverBg }}>
                    <Td>
                      <Text fontWeight="medium" color={textColor}>
                        {`${stat.serviceName} (${stat.server})`}
                      </Text>
                    </Td>
                    <Td textAlign="center">
                      <Flex align="center" justify="center" gap={2}>
                        <Icon as={FiCheckCircle} color="green.500" />
                        <Text>{stat.totalSuccess.toLocaleString()}</Text>
                      </Flex>
                    </Td>
                    <Td textAlign="center">
                      <Flex align="center" justify="center" gap={2}>
                        <Icon as={FiXCircle} color="red.500" />
                        <Text>{stat.totalFailed.toLocaleString()}</Text>
                      </Flex>
                    </Td>
                    <Td textAlign="center">
                      <Text>{(stat.totalFailed + stat.totalSuccess).toLocaleString()}</Text>
                    </Td>
                    <Td textAlign="center">
                      <Badge
                        px={2}
                        py={1}
                        // variant={'outline'}
                        // borderRadius="full"
                        bg={'transparent'}
                        border={'non'}
                        // bg={getSuccessRateBg(((stat.totalSuccess / (stat.totalSuccess + stat.totalFailed)) * 100).toFixed(1))}
                        color={getSuccessRateColor(((stat.totalSuccess / (stat.totalSuccess + stat.totalFailed)) * 100).toFixed(1))}
                      >
                        {((stat.totalSuccess / (stat.totalSuccess + stat.totalFailed)) * 100).toFixed(1)}%
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          {stats.length === 0 && (
            <Box textAlign="center" py={12}>
              <Icon as={FiBarChart2} boxSize={12} color="gray.400" />
              <Text mt={2} fontWeight="medium" color={textColor}>No services found</Text>
              <Text mt={1} color={subTextColor}>Try adjusting your search or filter criteria.</Text>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

const StatsCard = ({ icon, iconColor, label, value, valueColor, bg }) => {
  return (
    <Box bg={bg} p={[3, 4, 6]} borderRadius="lg" boxShadow="sm">
      <Flex align="center">
        <Box flexShrink={0}>
          <Icon as={icon} boxSize={[6, 7, 8]} color={iconColor} />
        </Box>
        <Box ml={[2, 3, 4]} flex={1} minW={0}>
          <Text fontSize={['xs', 'sm']} fontWeight="medium" color="gray.500" isTruncated>
            {label}
          </Text>
          <Text fontSize={['lg', 'xl', '2xl']} fontWeight="semibold" color={valueColor || 'inherit'}>
            {value}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};

export default DailyStats;